import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { SERVICES } from "@/lib/assets";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/sustainability", label: "Sustainability & CSR" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1600px] px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_vessel-connect-11/artifacts/wtxo2sei_freight-iq-lockup-horizontal-colour-on-dark-1024w-PLACEHOLDER-FONT.png"
            alt="Freight IQ"
            className="h-9 sm:h-10 w-auto group-hover:opacity-80 transition-opacity duration-300"
          />
        </Link>

        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {LINKS.map((l) =>
            l.label === "Services" ? (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <Link
                  to={l.to}
                  data-testid="nav-services"
                  className={`flex items-center gap-1 font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${
                    pathname.startsWith("/services") ? "text-primary" : "text-white/70 hover:text-white"
                  }`}
                >
                  {l.label} <ChevronDown size={12} className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
                </Link>
                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 pt-4 w-72"
                      data-testid="nav-services-dropdown"
                    >
                      <div className="bg-[#0a0a0a] border border-white/10 backdrop-blur-xl">
                        {SERVICES.map((s) => (
                          <Link
                            key={s.key}
                            to={`/services/${s.key}`}
                            data-testid={`nav-service-${s.key}`}
                            className="flex items-center justify-between px-5 py-3.5 border-b border-white/5 last:border-0 text-white/70 hover:text-white hover:bg-primary/10 transition-colors duration-200 group"
                          >
                            <span className="font-mono text-xs uppercase tracking-widest">{s.title}</span>
                            <span className="font-mono text-[10px] text-primary opacity-0 group-hover:opacity-100 transition-opacity">{s.num}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${
                  pathname === l.to ? "text-primary" : "text-white/70 hover:text-white"
                }`}
              >
                {l.label}
              </Link>
            )
          )}
        </div>

        <Link
          to="/contact"
          data-testid="nav-quote-btn"
          className="hidden md:flex items-center gap-2 hover-fill px-5 py-2.5 font-mono text-xs uppercase tracking-widest"
        >
          Get a Quote <ArrowUpRight size={14} />
        </Link>

        <button data-testid="nav-mobile-toggle" className="md:hidden text-white" onClick={() => setOpen((o) => !o)}>
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#050505] border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                l.label === "Services" ? (
                  <div key={l.to} className="flex flex-col gap-3">
                    <Link to={l.to} className="font-display text-2xl uppercase" data-testid="nav-mobile-services">
                      Services
                    </Link>
                    <div className="pl-4 border-l border-primary/40 flex flex-col gap-3">
                      {SERVICES.map((s) => (
                        <Link key={s.key} to={`/services/${s.key}`} className="font-mono text-xs uppercase tracking-widest text-white/60 hover:text-primary transition-colors" data-testid={`nav-mobile-service-${s.key}`}>
                          {s.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link key={l.to} to={l.to} className="font-display text-2xl uppercase" data-testid={`nav-mobile-${l.label.toLowerCase()}`}>
                    {l.label}
                  </Link>
                )
              ))}
              <Link to="/contact" className="hover-fill px-5 py-3 font-mono text-xs uppercase tracking-widest text-center mt-2">
                Get a Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
